import React, { useState, useEffect, useCallback } from 'react';
import {
  listEnhancementTypes,
  createEnhancementType,
  updateEnhancementType,
  deleteEnhancementType,
  EnhancementType,
} from '../../api/admin';
import './Admin.css';

const EnhancementTypes: React.FC = () => {
  const [types, setTypes] = useState<EnhancementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; type?: EnhancementType } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEnhancementTypes();
      setTypes(data);
    } catch (err) {
      setError('Failed to load enhancement types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const openModal = (mode: 'create' | 'edit', type?: EnhancementType) => {
    setModal({ mode, type });
    setName(type?.name || '');
    setDisplayName(type?.display_name || '');
    setSortOrder(type?.sort_order || types.length + 1);
  };

  const handleSave = async () => {
    if (!name.trim() || !displayName.trim()) return;
    setSaving(true);
    try {
      if (modal?.mode === 'create') {
        await createEnhancementType({
          name: name.toLowerCase().replace(/\s+/g, '_'),
          display_name: displayName,
          sort_order: sortOrder,
        });
      } else if (modal?.type) {
        await updateEnhancementType(modal.type.id, {
          name: name.toLowerCase().replace(/\s+/g, '_'),
          display_name: displayName,
          sort_order: sortOrder,
        });
      }
      await fetchTypes();
      setModal(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save enhancement type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm === null) return;
    setSaving(true);
    try {
      await deleteEnhancementType(deleteConfirm);
      await fetchTypes();
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete enhancement type');
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-empty"><p>Loading...</p></div>;
  }

  return (
    <div>
      <div className="admin-page-header">
        <h2>Enhancement Types</h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={() => openModal('create')}
        >
          Add Type
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-alert admin-alert-info" style={{ marginBottom: '20px' }}>
        Enhancement types categorize enhancements (e.g., Shirt, Pants, Headwear). Each character can only have one enhancement per type.
      </div>

      {types.length === 0 ? (
        <div className="admin-empty">
          <p>No enhancement types yet</p>
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => openModal('create')}
          >
            Add Type
          </button>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Display Name</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr key={type.id}>
                  <td>{type.name}</td>
                  <td>{type.display_name}</td>
                  <td>{type.sort_order}</td>
                  <td>
                    <div className="admin-btn-group">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-small"
                        onClick={() => openModal('edit', type)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-small"
                        onClick={() => setDeleteConfirm(type.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>{modal.mode === 'create' ? 'Add Enhancement Type' : 'Edit Enhancement Type'}</h3>
            <div className="admin-form-group">
              <label>Name (internal, lowercase)</label>
              <input
                type="text"
                className="admin-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., shirt, pants, headwear"
                maxLength={50}
              />
            </div>
            <div className="admin-form-group">
              <label>Display Name</label>
              <input
                type="text"
                className="admin-form-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Shirt, Pants, Headwear"
                maxLength={50}
              />
            </div>
            <div className="admin-form-group">
              <label>Sort Order</label>
              <input
                type="number"
                className="admin-form-input"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                min={0}
              />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving || !name.trim() || !displayName.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Confirm Delete</h3>
            <p className="confirm-dialog-text">
              Are you sure you want to delete this enhancement type?
              This cannot be undone.
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancementTypes;
